import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Switch,
} from "@mui/material";
import { Send, Phone, Group, People, Science } from "@mui/icons-material";

interface User {
  user_id: string;
  username: string;
  email: string;
  whatsapp_number: string | null;
  role: string;
}

interface MessageResponse {
  success: boolean;
  message: string;
  results?: { [key: string]: boolean };
  total_sent?: number;
  total_failed?: number;
}

const WhatsAppMessaging: React.FC = () => {
  const [message, setMessage] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [sendMode, setSendMode] = useState<
    "single" | "multiple" | "all" | "no_whatsapp"
  >("single");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<MessageResponse | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [emailHistory, setEmailHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load users on component mount
  useEffect(() => {
    loadUsers();
    loadEmailHistory();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await fetch("/api/whatsapp/users/all", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUsers(userData);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadEmailHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch("/api/whatsapp-verification/email-history", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setEmailHistory(data.emails || []);
      }
    } catch (error) {
      console.error("Error loading email history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) {
      alert("Please enter a message");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      let requestBody: any = { message: message.trim() };

      // Determine request based on send mode
      switch (sendMode) {
        case "single":
          if (phoneNumber.trim()) {
            requestBody.to_phone_number = phoneNumber.trim();
          } else if (selectedUsers.length === 1) {
            requestBody.to_user_id = selectedUsers[0];
          } else {
            alert("Please select a user or enter a phone number");
            setLoading(false);
            return;
          }
          break;
        case "multiple":
          if (selectedUsers.length === 0) {
            alert("Please select at least one user");
            setLoading(false);
            return;
          }
          requestBody.to_user_ids = selectedUsers;
          break;
        case "all":
          requestBody.send_to_all = true;
          break;
        case "no_whatsapp":
          // For users without WhatsApp, we'll call the verification email endpoint with force_send=true
          await sendVerificationEmails(true);
          return; // Exit early since sendVerificationEmails handles its own response
      }

      const response = await fetch("/api/whatsapp/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();
      setResult(responseData);
    } catch (error) {
      console.error("Error sending message:", error);
      setResult({
        success: false,
        message: "Failed to send message. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/whatsapp/test", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const responseData = await response.json();
      setResult(responseData);
    } catch (error) {
      console.error("Error testing connection:", error);
      setResult({
        success: false,
        message: "Failed to test WhatsApp connection",
      });
    } finally {
      setLoading(false);
    }
  };

  const sendVerificationEmails = async (forceSend: boolean = false) => {
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(
        "/api/whatsapp-verification/send-verification-emails",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ force_send: forceSend }),
        }
      );

      const responseData = await response.json();
      setResult(responseData);

      // Refresh email history after sending emails
      await loadEmailHistory();
    } catch (error) {
      console.error("Error sending verification emails:", error);
      setResult({
        success: false,
        message: "Failed to send verification emails. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const usersWithWhatsApp = users.filter((user) => user.whatsapp_number);
  const usersWithoutWhatsApp = users.filter((user) => !user.whatsapp_number);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        📱 WhatsApp Messaging
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Send WhatsApp Message
        </Typography>

        {/* Message Input */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter your WhatsApp message here..."
          sx={{ mb: 3 }}
        />

        {/* Send Mode Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Send Mode</InputLabel>
          <Select
            value={sendMode}
            onChange={(e) => setSendMode(e.target.value as any)}
            label="Send Mode"
          >
            <MenuItem value="single">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Phone sx={{ mr: 1 }} />
                Send to Single User/Phone Number
              </Box>
            </MenuItem>
            <MenuItem value="multiple">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Group sx={{ mr: 1 }} />
                Send to Multiple Users
              </Box>
            </MenuItem>
            <MenuItem value="all">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <People sx={{ mr: 1 }} />
                Send to All Users
              </Box>
            </MenuItem>
            <MenuItem value="no_whatsapp">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Send sx={{ mr: 1 }} />
                Send Verification Emails to Users Without WhatsApp
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        {/* Phone Number Input (for single mode) */}
        {sendMode === "single" && (
          <TextField
            fullWidth
            label="Phone Number (optional)"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1234567890 or leave empty to select from users"
            sx={{ mb: 3 }}
          />
        )}

        {/* User Selection */}
        {sendMode === "single" && !phoneNumber && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUsers[0] || ""}
              onChange={(e) => setSelectedUsers([e.target.value])}
              label="Select User"
            >
              {usersWithWhatsApp.map((user) => (
                <MenuItem key={user.user_id} value={user.user_id}>
                  {user.username} ({user.whatsapp_number})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Multiple User Selection */}
        {sendMode === "multiple" && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select Users ({selectedUsers.length} selected)
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: "auto",
                border: 1,
                borderColor: "divider",
                p: 1,
              }}
            >
              {usersWithWhatsApp.map((user) => (
                <Box
                  key={user.user_id}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Switch
                    checked={selectedUsers.includes(user.user_id)}
                    onChange={() => handleUserToggle(user.user_id)}
                    size="small"
                  />
                  <Typography variant="body2">
                    {user.username} ({user.whatsapp_number}) - {user.role}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Users Without WhatsApp Info */}
        {sendMode === "no_whatsapp" && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This will send verification emails to all users who don't have
                WhatsApp numbers configured. Users will receive an email with a
                link to add and verify their WhatsApp number.
              </Typography>
            </Alert>
            <Typography variant="subtitle1" gutterBottom>
              Users without WhatsApp numbers ({usersWithoutWhatsApp.length}):
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: "auto",
                border: 1,
                borderColor: "divider",
                p: 1,
                backgroundColor: "grey.50",
              }}
            >
              {usersWithoutWhatsApp.map((user) => (
                <Typography key={user.user_id} variant="body2" sx={{ mb: 0.5 }}>
                  • {user.username} ({user.email}) - {user.role}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          >
            {loading
              ? "Sending..."
              : sendMode === "no_whatsapp"
              ? "Send Verification Emails"
              : "Send Message"}
          </Button>

          <Button
            variant="outlined"
            onClick={testConnection}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Science />}
          >
            Test Connection
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={() => sendVerificationEmails(true)}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          >
            Send Verification Emails
          </Button>
        </Box>

        {/* Result Display */}
        {result && (
          <Alert severity={result.success ? "success" : "error"} sx={{ mb: 2 }}>
            {result.message}
            {result.total_sent !== undefined && result.total_sent !== null && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Sent: {result.total_sent} | Failed: {result.total_failed}
              </Typography>
            )}
          </Alert>
        )}
      </Paper>

      {/* User Statistics */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          User Statistics
        </Typography>

        {loadingUsers ? (
          <CircularProgress />
        ) : (
          <Box>
            <Typography variant="body1" gutterBottom>
              📱 Users with WhatsApp: {usersWithWhatsApp.length}
            </Typography>
            <Typography variant="body1" gutterBottom>
              📵 Users without WhatsApp: {usersWithoutWhatsApp.length}
            </Typography>
            <Typography variant="body1" gutterBottom>
              👥 Total Users: {users.length}
            </Typography>

            {usersWithoutWhatsApp.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Users without WhatsApp numbers:
                </Typography>
                <List dense>
                  {usersWithoutWhatsApp.slice(0, 5).map((user) => (
                    <ListItem key={user.user_id}>
                      <ListItemText
                        primary={user.username}
                        secondary={user.email}
                      />
                    </ListItem>
                  ))}
                  {usersWithoutWhatsApp.length > 5 && (
                    <ListItem>
                      <ListItemText
                        primary={`... and ${
                          usersWithoutWhatsApp.length - 5
                        } more`}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Email History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          📧 Email Sending History
        </Typography>

        {loadingHistory ? (
          <CircularProgress />
        ) : (
          <Box>
            <Typography variant="body1" gutterBottom>
              Total emails sent: {emailHistory.length}
            </Typography>

            {emailHistory.length > 0 ? (
              <Box sx={{ maxHeight: 600, overflow: "auto", mt: 2 }}>
                <List dense>
                  {emailHistory.map((email, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`${email.email_address} - ${email.status === 'sent' ? '✅ Sent' : email.status === 'failed' ? '❌ Failed' : email.status}`}
                        secondary={`${email.email_type} - ${new Date(
                          email.sent_at
                        ).toLocaleString()}`}
                      />
                      {email.error_message && (
                        <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                          Error: {email.error_message}
                        </Typography>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No emails sent yet
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default WhatsAppMessaging;
